import { IElement } from "core-integration/src/store/elements/types";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { useSelector } from "react-redux";
import styled from "styled-components";
import i18n from "../../../i18n";
import playMakerEn1x from "../../../img/playmaker-ad-en@1x.png";
import playMakerEn2x from "../../../img/playmaker-ad-en@2x.png";
import playMakerSv1x from "../../../img/playmaker-ad-sv@1x.png";
import playMakerSv2x from "../../../img/playmaker-ad-sv@2x.png";
import playMakerWideEn1x from "../../../img/playmaker-ad-wide-en@1x.png";
import playMakerWideEn2x from "../../../img/playmaker-ad-wide-en@2x.png";
import playMakerWideSv1x from "../../../img/playmaker-ad-wide-sv@1x.png";
import playMakerWideSv2x from "../../../img/playmaker-ad-wide-sv@2x.png";
import { getPlaymakerUrl } from "../../../utils/playmaker";
import { Hyperlink } from "../../Link";
import PlayerWidget from "./PlayerWidget";

const PlayerAd = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  border-radius: ${({ theme }) => theme.radii[2]};
`;

const Image = styled.img`
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
`;

const PlayerAdBackground = styled.div`
  display: flex;
  background-color: #11152b;
  border-radius: ${({ theme }) => theme.radii[2]};
  padding: ${({ theme }) => theme.space[2]} 0;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    flex: 1 1 50%;
  }
`;

const PlayerWidgetBackground = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.blue};
  border-radius: ${({ theme }) => theme.radii[2]};
  align-content: center;
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    flex: 1 1 50%;
  }
`;

const WidgetWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  width: 100%;
  gap: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    flex-direction: row;
    gap: ${({ theme }) => theme.space[2]};
  }
`;

interface Props {
  selectorId: string;
  element: IElement;
  showWidgetSummary?: boolean;
  onWidgetChange?: (visible: boolean) => void;
}

const PlayerWidgetWithAd = ({
  selectorId,
  element,
  onWidgetChange,
  showWidgetSummary = true,
}: Props) => {
  const player = useSelector(getPlayerData);
  const isSv = i18n.language === "sv";
  const portraitImg1x = isSv ? playMakerSv1x : playMakerEn1x;
  const portraitImg2x = isSv ? playMakerSv2x : playMakerEn2x;
  const wideImg1x = isSv ? playMakerWideSv1x : playMakerWideEn1x;
  const wideImg2x = isSv ? playMakerWideSv2x : playMakerWideEn2x;
  const url = getPlaymakerUrl(player, "player_profile", element);

  return (
    <WidgetWrapper>
      <PlayerWidgetBackground>
        <PlayerWidget
          selectorId={selectorId}
          element={element}
          onWidgetChange={onWidgetChange}
          showWidgetSummary={showWidgetSummary}
        />
      </PlayerWidgetBackground>
      <PlayerAdBackground>
        <Hyperlink href={url} target="_blank" rel="noopener noreferrer">
          <PlayerAd>
            <picture>
              <source
                media="(max-width: 699px)"
                srcSet={`${portraitImg1x} 1x, ${portraitImg2x} 2x`}
              />
              <source
                media="(min-width: 700px) and (max-width: 1023px)"
                srcSet={`${wideImg1x} 1x, ${wideImg2x} 2x`}
              />
              <source
                media="(min-width: 1024px)"
                srcSet={`${portraitImg1x} 1x, ${portraitImg2x} 2x`}
              />
              <Image
                src={portraitImg1x}
                alt="PlaymakerAI advertisement for Allsvenskan"
              />
            </picture>
          </PlayerAd>
        </Hyperlink>
      </PlayerAdBackground>
    </WidgetWrapper>
  );
};

export default PlayerWidgetWithAd;
